import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { AlertsContext } from "providers/alerts";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "utils/api";
import { getErrorMessage } from "utils/errorHandler";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { useQueryClient } from "@tanstack/react-query";
import { getBorrowersQueryKey } from "hooks/useBorrowers";

const DeleteBorrowerBtn = ({ id }: { id: string }) => {
  const navigate = useNavigate();
  const { user } = useTenant();
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useContext(AlertsContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();

  const deleteBorrower = async () => {
    try {
      setLoading(true);
      await apiRequest("/supabase/users/delete", {
        id,
      });
      showSuccess({
        title: "Borrower Deleted",
        description: "The borrower & all associated data has now been deleted.",
      });
      queryClient.invalidateQueries({ queryKey: getBorrowersQueryKey() });
      navigate("/borrowers");
    } catch (error) {
      showError({
        title: "Deletion Failed",
        description: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const checkPermission = () => {
    if (hasPermission(user, permissionConstants.editBorrower)) {
      //permission check
      return false;
    }
    if (!isPermissionEnabled(user)) {
      // no permissions
      return false;
    }
    return true;
  };

  return (
    <Flex justifyContent="flex-end">
      <Button onClick={onOpen} variant="secondary" borderWidth="0px" isDisabled={checkPermission()}>
        Delete Borrower
      </Button>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Borrower</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete this borrower, and all data (including loans)
              associated with it?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="ghost" isLoading={loading} onClick={deleteBorrower}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default DeleteBorrowerBtn;
